import { DialogComponent } from '@theme/dialog';

// Generic timed popup.
//
// Extends the shared DialogComponent, which already provides showDialog/
// closeDialog, scroll locking, backdrop-click and ESC-to-close. This subclass
// only adds the two things specific to the popup: open after a delay, and show
// once per browser session (a sessionStorage flag, namespaced per section).

class GenericPopup extends DialogComponent {
  /** @type {ReturnType<typeof setTimeout> | null} */
  #timer = null;

  connectedCallback() {
    super.connectedCallback();

    // Theme editor preview: open immediately and skip the session gate so the
    // merchant can see it while editing.
    if (this.hasAttribute('data-preview')) {
      this.showDialog();
      return;
    }

    if (this.#hasBeenSeen()) return;

    // Record once it closes (however that happens — button, backdrop or ESC).
    this.addEventListener('dialog:close', () => this.#markSeen(), { once: true });

    const delay = Number(this.dataset.delay) || 0;
    this.#timer = setTimeout(() => this.showDialog(), delay);
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    if (this.#timer) clearTimeout(this.#timer);
  }

  #storageKey() {
    return `_generic_popup_seen_${this.dataset.sectionId || 'default'}`;
  }

  #hasBeenSeen() {
    try {
      return sessionStorage.getItem(this.#storageKey()) === '1';
    } catch {
      // sessionStorage can throw in private mode / sandboxed frames.
      return false;
    }
  }

  #markSeen() {
    try {
      sessionStorage.setItem(this.#storageKey(), '1');
    } catch {
      // Ignore — popup just shows again next load, which is acceptable.
    }
  }
}

if (!customElements.get('generic-popup')) {
  customElements.define('generic-popup', GenericPopup);
}
